import React from 'react';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import {
  Box,
  Button,
  ButtonProps,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Divider,
} from '@mui/material';
import DialogAriaWrapper from '../DialogAriaWrapper';

type JunoDialogBaseProps = {
  id: string;
  title: React.ReactNode;
  width?: number;
} & DialogProps;

type JunoDialogProps =
  | ({
      mode: 'form';
      /**
       * It is only required for mode 'form'
       */
      children: React.ReactNode;
      subtitle?: React.ReactNode;
      message?: React.ReactNode;
      primaryBtnProps?: LoadingButtonProps;
      secondaryBtnProps?: ButtonProps;
    } & JunoDialogBaseProps)
  | ({
      mode: 'delete';
      subtitle: React.ReactNode;
      message: React.ReactNode;
      primaryBtnProps: LoadingButtonProps;
      secondaryBtnProps: ButtonProps;
    } & JunoDialogBaseProps)
  | ({
      mode: 'confirmation';
      subtitle: React.ReactNode;
      message: React.ReactNode;
      primaryBtnProps: LoadingButtonProps;
      secondaryBtnProps: ButtonProps;
    } & JunoDialogBaseProps)
  | ({
      mode: 'info';
      subtitle: React.ReactNode;
      message: React.ReactNode;
      primaryBtnProps: LoadingButtonProps;
      secondaryBtnProps?: ButtonProps;
    } & JunoDialogBaseProps);

export const JunoDialog = ({
  id,
  open,
  title,
  subtitle,
  message,
  primaryBtnProps,
  secondaryBtnProps,
  mode,
  width = 600,
  ...props
}: JunoDialogProps) => {
  const children = mode === 'form' ? (props as { children: React.ReactNode }).children : undefined;
  const showContent = !!subtitle || !!message || !!children;

  return (
    <DialogAriaWrapper id={id} open={open} {...props}>
      <DialogTitle
        sx={{
          width,
          textAlign: 'center',
          justifyContent: mode !== 'form' ? 'center' : 'flex-start',
          display: 'flex',
          mx: mode !== 'form' ? 'auto' : '0',
        }}
      >
        {title}
      </DialogTitle>
      <Divider sx={{ mx: 2 }} />
      {showContent && (
        <>
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: mode === 'form' ? 'flex-start' : 'center',
              justifyContent: mode === 'form' ? 'flex-start' : 'center',
              gap: 1,
              textAlign: mode === 'form' ? 'left' : 'center',
              width,
              minHeight: 150,
              mx: 'auto',
            }}
          >
            <DialogContentText variant='subtitle1' component='div'>
              {subtitle}
            </DialogContentText>
            {message && (
              <DialogContentText
                variant='body2'
                component='div'
                color={mode === 'delete' ? 'error' : 'inherit'}
              >
                {message}
              </DialogContentText>
            )}
            {children && <Box sx={{ mt: 2, width: '100%' }}>{children}</Box>}
          </DialogContent>
          <Divider sx={{ mx: 2 }} />
        </>
      )}
      <DialogActions sx={{ p: 2, display: 'flex', gap: 2 }}>
        {primaryBtnProps?.title && (
          <LoadingButton
            aria-label={primaryBtnProps.title}
            variant='contained'
            fullWidth
            color={mode === 'delete' ? 'error' : 'primary'}
            {...primaryBtnProps}
          >
            {primaryBtnProps.title}
          </LoadingButton>
        )}
        {secondaryBtnProps?.title && (
          <Button
            aria-label={secondaryBtnProps.title}
            variant='outlined'
            color='primary'
            fullWidth
            {...secondaryBtnProps}
          >
            {secondaryBtnProps.title}
          </Button>
        )}
      </DialogActions>
    </DialogAriaWrapper>
  );
};
